import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Text } from '@react-three/drei';
import { useFrame } from '@react-three/fiber';
import { useEffect, useMemo, useRef, useState } from 'react';
import * as THREE from 'three';
import { POINTER_MOTION, POINTER_PIXELS, buildPointerRigTriangles, isPointerRigSettled, pointerCaptionAnchor, createGuideSlide, resolvePointerHome, sampleGuidePointer, sampleGuideSlide, samplePointerSweep, stepPointerRig, stepPointerSweep, } from '../_pure/emphasisPointer.js';
const FORWARD = new THREE.Vector3();
const OFFSET = new THREE.Vector3();
const POINTER_RENDER_ORDER = 10_000;
const INITIAL_VERTICES = 1024;
const CAPTION_FADE_RATE = 10;
const CAPTION_VANISH = 0.02;
/** Converts screen pixels to world units at a point, for perspective and orthographic cameras alike. */
function worldPerPixelFor(camera, height) {
    if (camera instanceof THREE.OrthographicCamera) {
        const perPixel = (camera.top - camera.bottom) / camera.zoom / height;
        return () => perPixel;
    }
    const factor = 2 / camera.projectionMatrix.elements[5] / height;
    camera.getWorldDirection(FORWARD);
    const forward = FORWARD.clone();
    const eye = camera.getWorldPosition(new THREE.Vector3());
    const near = camera.near;
    return point => Math.max(near, OFFSET.set(point[0], point[1], point[2]).sub(eye).dot(forward)) * factor;
}
/**
 * The arrow rig: a hub that flies to its placement and arms that retract and reach toward the pointed targets.
 * It persists across guidance changes and shrinks away when nothing is pointed at.
 */
export function EmphasisPointer(props) {
    const pointerRef = useRef(props.pointer);
    pointerRef.current = props.pointer;
    const state = useRef(undefined);
    const sweepTime = useRef(undefined);
    const guideSlide = useRef(undefined);
    const meshRef = useRef(null);
    const materialRef = useRef(null);
    const captionRef = useRef(null);
    const captionMaterialRef = useRef(null);
    const captionOpacity = useRef(0);
    const shownCaption = useRef(undefined);
    const [caption, setCaption] = useState(undefined);
    const geometry = useMemo(() => {
        const created = new THREE.BufferGeometry();
        created.setAttribute('position', new THREE.BufferAttribute(new Float32Array(INITIAL_VERTICES * 3), 3)
            .setUsage(THREE.DynamicDrawUsage));
        created.setDrawRange(0, 0);
        return created;
    }, []);
    useEffect(() => () => geometry.dispose(), [geometry]);
    useFrame(({ camera, size }, delta) => {
        const pointer = pointerRef.current;
        const seconds = props.paused ? 0 : Math.min(delta, 0.1);
        const rotation = camera.getWorldQuaternion(new THREE.Quaternion());
        const eye = camera.getWorldPosition(new THREE.Vector3());
        const up = new THREE.Vector3(0, 1, 0).applyQuaternion(rotation);
        const forward = camera.getWorldDirection(new THREE.Vector3());
        const view = {
            eye: [eye.x, eye.y, eye.z],
            up: [up.x, up.y, up.z],
            forward: [forward.x, forward.y, forward.z],
            orthographic: camera instanceof THREE.OrthographicCamera,
            worldPerPixel: worldPerPixelFor(camera, size.height),
        };
        const slideDuration = pointer?.guide?.slideDurationMs;
        if (pointer?.guide && slideDuration !== undefined) {
            if (guideSlide.current?.key !== pointer.guide.key) {
                const slide = createGuideSlide(pointer.guide, view.eye);
                guideSlide.current = { ...slide, home: resolvePointerHome(pointer.placement, slide.focus, view.eye, view.up),
                    durationMs: slideDuration, elapsedMs: 0 };
            }
        }
        else
            guideSlide.current = undefined;
        const slide = guideSlide.current;
        if (slide && !props.transitionActive && state.current?.presence === 1
            && state.current.arms.some(arm => arm.key === slide.key && arm.active && arm.progress === 1)) {
            slide.elapsedMs = Math.min(slide.durationMs, slide.elapsedMs + seconds * 1000);
        }
        const guide = pointer?.guide && !slide ? sampleGuidePointer(pointer.guide, view.eye) : undefined;
        const home = slide?.home ?? (pointer ? resolvePointerHome(pointer.placement, guide?.focus ?? pointer.focus, view.eye, view.up)
            : state.current?.hub ?? view.eye);
        const hubTolerance = guide ? Infinity : POINTER_MOTION.settledPixels * view.worldPerPixel(home);
        const sweep = pointer?.sweep;
        sweepTime.current = sweep ? stepPointerSweep(sweepTime.current, sweep, seconds * 1000, !props.transitionActive && state.current?.presence === 1
            && state.current.arms.some(arm => arm.active && arm.progress === 1)) : undefined;
        const arms = slide ? [sampleGuideSlide(slide, slide.elapsedMs / slide.durationMs)]
            : guide ? [guide.arm] : sweep ? [samplePointerSweep(sweep, (sweepTime.current?.elapsedMs ?? 0) / sweep.durationMs)] : pointer?.arms ?? [];
        state.current = stepPointerRig(state.current, { home, arms, hubTolerance: sweep ? Infinity : hubTolerance }, seconds);
        const current = state.current;
        if (pointer && materialRef.current)
            materialRef.current.color.set(pointer.color);
        const shown = current && guide ? { ...current, presence: current.presence * guide.visibility,
            arms: current.arms.map(arm => ({ ...arm, progress: arm.progress * guide.visibility })) } : current;
        const triangles = shown ? buildPointerRigTriangles(shown, view) : [];
        let attribute = geometry.getAttribute('position');
        if (triangles.length > attribute.count) {
            attribute = new THREE.BufferAttribute(new Float32Array(triangles.length * 2 * 3), 3).setUsage(THREE.DynamicDrawUsage);
            geometry.setAttribute('position', attribute);
        }
        triangles.forEach((point, index) => attribute.setXYZ(index, point[0], point[1], point[2]));
        attribute.needsUpdate = true;
        geometry.setDrawRange(0, triangles.length);
        if (meshRef.current)
            meshRef.current.visible = triangles.length > 0;
        const wanted = pointer?.caption;
        const visible = wanted !== undefined && wanted === shownCaption.current && (guide?.visibility ?? 1) === 1
            && isPointerRigSettled(current, home, hubTolerance);
        captionOpacity.current += ((visible ? 1 : 0) - captionOpacity.current) * (1 - Math.exp(-CAPTION_FADE_RATE * seconds));
        if (captionOpacity.current < CAPTION_VANISH && shownCaption.current !== wanted) {
            shownCaption.current = wanted;
            setCaption(wanted);
        }
        const group = captionRef.current;
        if (group) {
            group.visible = current !== undefined && captionOpacity.current >= CAPTION_VANISH;
            if (current && group.visible) {
                const anchor = pointerCaptionAnchor(current, view);
                group.position.set(anchor[0], anchor[1], anchor[2]);
                group.scale.setScalar(view.worldPerPixel(anchor));
                group.quaternion.copy(rotation);
            }
        }
        if (captionMaterialRef.current) {
            captionMaterialRef.current.opacity = captionOpacity.current;
            if (pointer)
                captionMaterialRef.current.color.set(pointer.color);
        }
    });
    return (_jsxs(_Fragment, { children: [_jsx("mesh", { ref: meshRef, geometry: geometry, frustumCulled: false, visible: false, renderOrder: POINTER_RENDER_ORDER, children: _jsx("meshBasicMaterial", { ref: materialRef, transparent: true, depthTest: false, depthWrite: false, side: THREE.DoubleSide, toneMapped: false }) }), _jsx("group", { ref: captionRef, visible: false, children: caption === undefined ? null : (_jsxs(Text, { fontSize: POINTER_PIXELS.captionSize, anchorX: "center", anchorY: "bottom", renderOrder: POINTER_RENDER_ORDER, children: [_jsx("meshBasicMaterial", { ref: captionMaterialRef, transparent: true, opacity: 0, depthTest: false, depthWrite: false, toneMapped: false }), caption] })) })] }));
}
